import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { PageHeader, Card, CardHeader, Button, Spinner } from '@orientamos/ui'
import { FileText, FileCheck, Plus, Eye, Clock } from 'lucide-react'

// Types
interface ActivityItem {
  id: string
  type: string
  entityId: string
  action: string
  description: string
  timestamp: string
}

interface ChartData {
  statusDistribution: Array<{ status: string; count: number }>
  contractsByMonth: Array<{ month: string; count: number }>
}

// Status labels in Spanish
const STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  review: 'En revision',
  approved: 'Aprobado',
  sent: 'Enviado',
  signed: 'Firmado',
  archived: 'Archivado',
}

// Status colors for pie chart
const STATUS_COLORS: Record<string, string> = {
  draft: '#9CA3AF', // neutral-400
  review: '#F59E0B', // warning
  approved: '#10B981', // success
  sent: '#3B82F6', // primary
  signed: '#8B5CF6', // purple
  archived: '#6B7280', // neutral-500
}

// Format relative time in Spanish
function formatRelativeTime(timestamp: string): string {
  const now = new Date()
  const date = new Date(timestamp)
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'hace un momento'
  if (diffMins < 60) return `hace ${diffMins} min`
  if (diffHours < 24) return `hace ${diffHours} h`
  if (diffDays < 7) return `hace ${diffDays} dias`
  return date.toLocaleDateString('es-CO')
}

// Simple SVG Pie Chart component
function PieChart({ data }: { data: Array<{ status: string; count: number }> }) {
  const total = data.reduce((sum, item) => sum + item.count, 0)
  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-neutral-400">
        Sin datos disponibles
      </div>
    )
  }

  let currentAngle = 0
  const slices = data.map((item) => {
    const angle = (item.count / total) * 360
    const startAngle = currentAngle
    const endAngle = currentAngle + angle
    currentAngle = endAngle

    const startRad = ((startAngle - 90) * Math.PI) / 180
    const endRad = ((endAngle - 90) * Math.PI) / 180

    const x1 = 50 + 40 * Math.cos(startRad)
    const y1 = 50 + 40 * Math.sin(startRad)
    const x2 = 50 + 40 * Math.cos(endRad)
    const y2 = 50 + 40 * Math.sin(endRad)

    const largeArc = angle > 180 ? 1 : 0

    const path =
      angle === 360
        ? `M 50 10 A 40 40 0 1 1 49.99 10 Z`
        : `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`

    return {
      ...item,
      path,
      color: STATUS_COLORS[item.status] || '#9CA3AF',
    }
  })

  return (
    <div className="flex items-center gap-6">
      <svg viewBox="0 0 100 100" className="w-48 h-48">
        {slices.map((slice, i) => (
          <path key={i} d={slice.path} fill={slice.color} stroke="white" strokeWidth="1" />
        ))}
      </svg>
      <div className="flex flex-col gap-2">
        {slices.map((slice, i) => (
          <div key={i} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: slice.color }} />
            <span className="text-neutral-600">
              {STATUS_LABELS[slice.status] || slice.status}: {slice.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Simple SVG Bar Chart component
function BarChart({ data }: { data: Array<{ month: string; count: number }> }) {
  const maxCount = Math.max(...data.map((d) => d.count), 1)
  const barWidth = 100 / data.length - 2

  return (
    <div className="h-48">
      <svg viewBox="0 0 100 60" className="w-full h-40" preserveAspectRatio="xMidYMid meet">
        {/* Y-axis grid lines */}
        {[0, 25, 50, 75, 100].map((pct) => (
          <line
            key={pct}
            x1="10"
            y1={50 - (pct / 100) * 45}
            x2="95"
            y2={50 - (pct / 100) * 45}
            stroke="#E5E7EB"
            strokeWidth="0.2"
          />
        ))}

        {/* Bars */}
        {data.map((item, i) => {
          const barHeight = (item.count / maxCount) * 45
          const x = 12 + i * (barWidth + 2)
          const y = 50 - barHeight

          return (
            <g key={i}>
              <rect x={x} y={y} width={barWidth} height={barHeight} fill="#3B82F6" rx="1" />
              {/* Value label */}
              {item.count > 0 && (
                <text
                  x={x + barWidth / 2}
                  y={y - 2}
                  textAnchor="middle"
                  fontSize="3"
                  fill="#6B7280"
                >
                  {item.count}
                </text>
              )}
            </g>
          )
        })}
      </svg>
      {/* X-axis labels */}
      <div className="flex justify-between px-2 text-xs text-neutral-500">
        {data.map((item, i) => (
          <span key={i} className="text-center" style={{ width: `${100 / data.length}%` }}>
            {item.month.split(' ')[0]}
          </span>
        ))}
      </div>
    </div>
  )
}

export default function DashboardPage() {
  const navigate = useNavigate()
  const { getToken } = useAuth()
  const [activity, setActivity] = useState<ActivityItem[]>([])
  const [chartData, setChartData] = useState<ChartData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = await getToken()
        const headers = { Authorization: `Bearer ${token}` }
        const apiUrl = import.meta.env.VITE_API_URL || ''

        // Fetch all dashboard data in parallel
        const [activityRes, chartsRes] = await Promise.all([
          fetch(`${apiUrl}/api/dashboard/activity`, { headers }),
          fetch(`${apiUrl}/api/dashboard/charts`, { headers }),
        ])

        if (activityRes.ok) {
          const activityData = await activityRes.json()
          if (activityData.success) {
            setActivity(activityData.data.items || [])
          }
        }

        if (chartsRes.ok) {
          const chartsData = await chartsRes.json()
          if (chartsData.success) {
            setChartData(chartsData.data)
          }
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err)
        setError('Error al cargar el inicio')
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [getToken])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger-600">{error}</p>
        <Button variant="outline" className="mt-4" onClick={() => window.location.reload()}>
          Reintentar
        </Button>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Inicio"
        description="Resumen de actividad y acceso rapido a las funciones principales"
      />

      {/* Quick Actions */}
      <div className="flex gap-3 mb-6">
        <Button onClick={() => navigate('/intakes/new')}>
          <Plus className="w-4 h-4 mr-2" />
          Nueva Orden
        </Button>
        <Button variant="outline" onClick={() => navigate('/contracts')}>
          <Eye className="w-4 h-4 mr-2" />
          Ver Contratos
        </Button>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <CardHeader title="Estado de Contratos" description="Distribucion por estado actual" />
          <div className="mt-4">
            {chartData?.statusDistribution && chartData.statusDistribution.length > 0 ? (
              <PieChart data={chartData.statusDistribution} />
            ) : (
              <div className="flex items-center justify-center h-48 text-neutral-400">
                Sin contratos registrados
              </div>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Contratos por Mes" description="Ultimos 6 meses" />
          <div className="mt-4">
            {chartData?.contractsByMonth && chartData.contractsByMonth.length > 0 ? (
              <BarChart data={chartData.contractsByMonth} />
            ) : (
              <div className="flex items-center justify-center h-48 text-neutral-400">
                Sin datos disponibles
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader title="Actividad Reciente" description="Ultimas acciones en el sistema" />
        <div className="mt-4">
          {activity.length > 0 ? (
            <div className="divide-y divide-neutral-100">
              {activity.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-4 py-3 cursor-pointer hover:bg-neutral-50 rounded-lg px-2 -mx-2"
                  onClick={() => {
                    if (item.type === 'intake') {
                      navigate(`/intakes/${item.entityId}`)
                    } else if (item.type === 'contract') {
                      navigate(`/contracts/${item.entityId}`)
                    } else if (item.type === 'template') {
                      navigate(`/templates/${item.entityId}`)
                    }
                  }}
                >
                  <div className="flex-shrink-0 p-2 bg-neutral-100 rounded-lg">
                    {item.type === 'intake' && <FileText className="w-4 h-4 text-neutral-600" />}
                    {item.type === 'contract' && <FileCheck className="w-4 h-4 text-neutral-600" />}
                    {item.type === 'template' && <FileText className="w-4 h-4 text-neutral-600" />}
                    {item.type === 'clause' && <FileText className="w-4 h-4 text-neutral-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900">{item.description}</p>
                    <p className="text-xs text-neutral-500 truncate">
                      ID: {item.entityId.slice(0, 8)}...
                    </p>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-neutral-400">
                    <Clock className="w-3 h-3" />
                    {formatRelativeTime(item.timestamp)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-neutral-400">
              <Clock className="w-8 h-8 mb-2" />
              <p className="text-sm">Sin actividad reciente</p>
              <p className="text-xs mt-1">Las acciones que realices apareceran aqui</p>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
